import React, { useState } from 'react';
import { Crown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';

export function SignUpForm() {
  const navigate = useNavigate();
  const today = new Date();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [month, setMonth] = useState(today.toLocaleString('default', { month: 'long' }));
  const [day, setDay] = useState(today.getDate().toString());
  const [year, setYear] = useState(today.getFullYear().toString());
  const [gender, setGender] = useState('');
  const [customGender, setCustomGender] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
  const currentYear = today.getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => (currentYear - i).toString());

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Sign up the user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: `${firstName} ${lastName}`,
            birthday: `${year}-${(months.indexOf(month) + 1).toString().padStart(2, '0')}-${day.padStart(2, '0')}`,
            gender: gender === 'Other' ? customGender : gender,
          },
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('No user data returned');

      // Sign in the user automatically
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;

      // Redirect to feed
      navigate('/');
    } catch (err) {
      console.error('Error during sign up:', err);
      if (err instanceof Error) {
        if (err.message.includes('user_already_exists')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An error occurred during sign up');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-royal-purple/10 to-royal-purple/5 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 animate-shimmer pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-center gap-4 text-royal-purple mb-8 animate-slide-up">
          <div className="relative">
            <div className="absolute inset-0 bg-royal-purple/20 rounded-full blur-xl animate-pulse-slow" />
            <Crown size={48} className="text-royal-purple animate-float relative" />
          </div>
          <h1 className="text-4xl font-bold text-royal-purple">
            RoyaltySuite
          </h1>
        </div>

        <div className="bg-white/90 glass-effect rounded-2xl shadow-lg overflow-hidden animate-scale-in hover-lift">
          <div className="border-b border-gray-200/50 text-center bg-gradient-to-r from-royal-purple/5 to-royal-purple/5">
            <h2 className="text-2xl font-semibold py-6 text-royal-purple">
              Create account
            </h2>
          </div>

          <form onSubmit={handleSignUp} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50/90 text-red-600 p-4 rounded-lg text-sm animate-fade-in">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="group">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 group-hover:shadow-md"
                    required
                  />
                </div>
                <div className="group">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 group-hover:shadow-md"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Birthday
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 hover:shadow-md"
                  >
                    {months.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 hover:shadow-md"
                  >
                    {days.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 hover:shadow-md"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Gender
                </label>
                <div className="space-y-2">
                  <div className="flex gap-6">
                    {['Female', 'Male', 'Other'].map((option) => (
                      <label key={option} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="radio"
                          name="gender"
                          value={option}
                          checked={gender === option}
                          onChange={(e) => setGender(e.target.value)}
                          className="w-4 h-4 accent-royal-purple focus:ring-royal-purple transition-transform group-hover:scale-110"
                          required
                        />
                        <span className="text-gray-700 group-hover:text-royal-purple transition-colors">
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                  {gender === 'Other' && (
                    <input
                      type="text"
                      value={customGender}
                      onChange={(e) => setCustomGender(e.target.value)}
                      placeholder="Gender"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 animate-fade-in hover:shadow-md"
                      required
                    />
                  )}
                </div>
              </div>

              <div className="group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 group-hover:shadow-md"
                  required
                />
              </div>

              <div className="group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all duration-300 hover:border-royal-purple/50 group-hover:shadow-md"
                  required
                />
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="w-4 h-4 rounded text-royal-purple focus:ring-royal-purple"
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" className="text-royal-purple hover:text-royal-purple/80 transition-colors">Terms of Service</Link>,{' '}
                    <Link to="/privacy" className="text-royal-purple hover:text-royal-purple/80 transition-colors">Privacy Policy</Link>, and{' '}
                    <Link to="/cookies" className="text-royal-purple hover:text-royal-purple/80 transition-colors">Cookie Policy</Link>
                  </span>
                </label>

                <p className="text-sm text-gray-600 leading-relaxed">
                  You may receive SMS Notifications from us and can opt out any time.
                </p>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-3 transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] bg-royal-purple hover:bg-royal-purple/90"
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-royal-purple hover:text-royal-purple/80 transition-colors duration-300 font-medium"
              >
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}